/**
 * @flow
 */

import React, { useEffect } from 'react';
import gql from 'graphql-tag';
import { useForm, Controller } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Icon, Message, Form, Input, Label } from 'semantic-ui-react';
import { Modal, Button } from '../../../../components';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import { GET_MANUFACTURERS_QUERY } from '../../containers/manufacturers/ManufacturersList';
import { get } from 'lodash';

type AddManufacturerDialogProps = {
    intl: Object,
    trigger?: any,
    open: boolean,
    onClose: () => void,
};

const intlMessages = defineMessages({
    manufacturerNamePlaceholder: {
        id: 'pages.settings.manufacturers.form.addForm.manufacturerName',
        defaultMessage: 'Manufacturer name',
    },
    manufacturerNameError: {
        id: 'pages.settings.manufacturers.form.addForm.manufacturerNameError',
        defaultMessage: 'Enter manufacturer name',
    },
    nameExistingsError: {
        id: 'pages.settings.manufacturers.form.nameExistingsError',
        defaultMessage: 'Manufacturer with the same name already exists',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const ADD_MANUFACTURER_MUTATION = gql`
    mutation addManufacturer($input: AddManufacturerInput!) {
        addManufacturer(input: $input) {
            manufacturer {
                id
                name
            }
        }
    }
`;

const AddManufacturerDialog = ({
    trigger,
    open,
    onClose,
    intl,
}: AddManufacturerDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));

    const {
        control,
        handleSubmit,
        reset,
        setError,
        formState: { errors, isSubmitting, isDirty },
    } = useForm({ defaultValues: { name: '' } });

    useEffect(() => {
        if (open) {
            reset({ name: '' });
        }
    }, [open, reset]);

    const [addManufacturer] = useMutation(ADD_MANUFACTURER_MUTATION);

    const onSubmit = async formData => {
        try {
            await addManufacturer({
                variables: {
                    input: {
                        salon,
                        ...formData,
                    },
                },
                update: (store, { data: { addManufacturer: result } }) => {
                    const data = store.readQuery({
                        query: GET_MANUFACTURERS_QUERY,
                        variables: { salon },
                    });

                    if (
                        data &&
                        data.viewer &&
                        data.viewer.salon &&
                        data.viewer.salon.manufacturers
                    ) {
                        store.writeQuery({
                            query: GET_MANUFACTURERS_QUERY,
                            variables: { salon },
                            data: {
                                ...data,
                                viewer: {
                                    ...data.viewer,
                                    salon: {
                                        ...data.viewer.salon,
                                        manufacturers: {
                                            ...data.viewer.salon.manufacturers,
                                            nodes: [
                                                ...data.viewer.salon.manufacturers.nodes,
                                                result.manufacturer,
                                            ],
                                        },
                                    },
                                },
                            },
                        });
                    }
                },
            });
            onClose();
        } catch (error) {
            if (error.graphQLErrors && error.graphQLErrors.length) {
                const graphQLError = error.graphQLErrors[0];
                if (
                    graphQLError.name === 'AlreadyExists' &&
                    get(graphQLError, 'data.error') === 'NAME_ALREADY_EXISTS'
                ) {
                    setError('name', {
                        message: intl.formatMessage(intlMessages.nameExistingsError),
                    });
                    return;
                }
            }
            setError('root', {
                message: intl.formatMessage(intlMessages.defaultErrorMessage),
            });
        }
    };

    return (
        <Modal size="small" trigger={trigger} open={open} onClose={onClose}>
            <Modal.Header>
                <FormattedMessage
                    id="pages.settings.manufacturers.form.addForm.addManufacturer"
                    defaultMessage="Add manufacturer"
                />
            </Modal.Header>
            <Modal.Content>
                {errors.root && (
                    <Message error>
                        <Icon name="warning sign" />
                        {errors.root.message}
                    </Message>
                )}
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Form.Field error={!!errors.name}>
                        <Controller
                            name="name"
                            control={control}
                            rules={{
                                required: intl.formatMessage(intlMessages.manufacturerNameError),
                            }}
                            render={({ field: { ref: _ref, ...field } }) => (
                                <Input
                                    {...field}
                                    placeholder={intl.formatMessage(
                                        intlMessages.manufacturerNamePlaceholder,
                                    )}
                                    fluid
                                />
                            )}
                        />
                        {errors.name && (
                            <Label color="red" pointing basic>
                                {errors.name.message}
                            </Label>
                        )}
                    </Form.Field>
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button
                    disabled={isSubmitting || !isDirty}
                    loading={isSubmitting}
                    onClick={handleSubmit(onSubmit)}
                    primary
                >
                    <Icon name="checkmark" />
                    <FormattedMessage
                        id="pages.settings.manufacturers.form.addForm.add"
                        defaultMessage="Add"
                    />
                </Button>
                <Button disabled={isSubmitting} onClick={onClose} basic>
                    <Icon name="remove" />
                    <FormattedMessage
                        id="pages.settings.manufacturers.form.addForm.cancel"
                        defaultMessage="Cancel"
                    />
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default injectIntl(AddManufacturerDialog);
